import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Spin, Empty, message } from 'antd';
import {
  AlertOutlined,
  AudioOutlined,
  CalendarOutlined,
  ContactsOutlined,
  EnvironmentOutlined,
  HeartOutlined,
  InboxOutlined,
  MedicineBoxOutlined,
  SearchOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { GiCow } from 'react-icons/gi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../services/api';
import useCurrentFarm from '../hooks/useCurrentFarm';
import { getGlobalSearchRoute } from '../utils/globalSearchRoutes';

const getSearchItemIcon = (type) => {
  switch (type) {
    case 'animal':
      return <GiCow />;
    case 'animal_group':
    case 'feedlot_batch':
      return <TeamOutlined />;
    case 'farm':
    case 'zone':
    case 'shed':
    case 'lot':
    case 'pen':
      return <EnvironmentOutlined />;
    case 'contact':
    case 'user':
    case 'visitor':
      return <ContactsOutlined />;
    case 'inventory':
    case 'warehouse':
      return <InboxOutlined />;
    case 'alert':
      return <AlertOutlined />;
    case 'breeding':
      return <HeartOutlined />;
    case 'treatment':
      return <MedicineBoxOutlined />;
    case 'schedule':
    case 'task':
      return <CalendarOutlined />;
    case 'yield':
    case 'milking':
      return <ThunderboltOutlined />;
    default:
      return <SearchOutlined />;
  }
};

const getSpeechRecognition = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const speechLanguageFromCode = (code) => {
  const map = {
    en: 'en-US',
    ur: 'ur-PK',
    ar: 'ar-SA',
    hi: 'hi-IN',
    pa: 'pa-IN',
    ps: 'ps-AF',
    sd: 'sd-PK',
    fa: 'fa-IR',
    bn: 'bn-BD',
    tr: 'tr-TR',
    'zh-CN': 'zh-CN',
    es: 'es-ES',
    fr: 'fr-FR',
    de: 'de-DE',
    pt: 'pt-PT',
    ru: 'ru-RU',
    id: 'id-ID',
    ms: 'ms-MY',
    it: 'it-IT',
    ja: 'ja-JP',
    ko: 'ko-KR',
    th: 'th-TH',
    vi: 'vi-VN',
    nl: 'nl-NL',
    sw: 'sw-KE',
  };

  return map[code] || code || 'en-US';
};

const VoiceSearchButton = ({ listening, onClick, disabled = false, className = '' }) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className={`global-search-voice-btn${listening ? ' is-listening' : ''}${className ? ` ${className}` : ''}`}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick?.(event);
      }}
      disabled={disabled}
      aria-label={listening ? t('common.voiceSearchStop') : t('common.voiceSearch')}
      title={listening ? t('common.voiceSearchListening') : t('common.voiceSearch')}
    >
      <AudioOutlined />
    </button>
  );
};

export const GlobalSearchBarTrigger = ({
  onClick,
  onVoiceClick,
  disabled = false,
  listening = false,
}) => {
  const { t } = useTranslation();
  const shortcutLabel = navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl+K';

  return (
    <div className="global-search-header-controls">
      <button
        type="button"
        className="global-search-trigger"
        onClick={onClick}
        disabled={disabled}
        aria-label={t('common.globalSearch')}
      >
        <SearchOutlined />
        <span className="global-search-trigger-text">{t('common.globalSearchPlaceholder')}</span>
        <kbd className="global-search-kbd">{shortcutLabel}</kbd>
      </button>
      <VoiceSearchButton
        listening={listening}
        disabled={disabled}
        onClick={onVoiceClick}
      />
    </div>
  );
};

export const GlobalSearchIconTrigger = ({
  onClick,
  disabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <Button
      type="text"
      icon={<SearchOutlined />}
      className="header-icon-btn global-search-icon-btn"
      onClick={onClick}
      disabled={disabled}
      aria-label={t('common.globalSearch')}
      title={t('common.globalSearch')}
    />
  );
};

const GlobalSearch = forwardRef(({
  disabled = false,
  open,
  onOpenChange,
  onListeningChange,
}, ref) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  // Search inside the sidebar farm when one is picked; "All farms" searches every farm.
  const { farmId: currentFarmId } = useCurrentFarm();
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const [query, setQuery] = useState('');
  const [apiResults, setApiResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [listening, setListening] = useState(false);

  const allSelectableItems = useMemo(() => {
    const items = [];

    apiResults.forEach((section) => {
      section.items.forEach((item) => {
        items.push({
          key: `${section.key}-${item.type}-${item.id}`,
          title: item.title,
          subtitle: item.subtitle || section.label,
          sectionLabel: section.label,
          record: item,
        });
      });
    });

    return items;
  }, [apiResults]);

  const stopListening = useCallback(() => {
    const recognition = recognitionRef.current;
    if (recognition) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.stop();
      } catch {
        // already stopped
      }
      recognitionRef.current = null;
    }
    setListening(false);
  }, []);

  const startListening = useCallback(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) {
      message.warning(t('common.voiceSearchUnsupported'));
      return;
    }

    stopListening();

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = speechLanguageFromCode(currentLanguage);
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let transcript = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        transcript += event.results[index][0].transcript;
      }
      setQuery(transcript.trim());
    };

    recognition.onerror = (event) => {
      setListening(false);
      recognitionRef.current = null;

      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        message.error(t('common.voiceSearchPermissionDenied'));
        return;
      }

      if (event.error === 'no-speech') {
        message.info(t('common.voiceSearchNoSpeech'));
      }
    };

    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setListening(true);
    } catch {
      message.warning(t('common.voiceSearchUnsupported'));
    }
  }, [currentLanguage, stopListening, t]);

  const toggleListening = useCallback(() => {
    if (listening) {
      stopListening();
      return;
    }
    startListening();
  }, [listening, startListening, stopListening]);

  useImperativeHandle(ref, () => ({
    startListening,
    stopListening,
    toggleListening,
  }), [startListening, stopListening, toggleListening]);

  const closeSearch = useCallback(() => {
    stopListening();
    onOpenChange?.(false);
    setQuery('');
    setApiResults([]);
    setLoading(false);
    setActiveIndex(0);
  }, [onOpenChange, stopListening]);

  const navigateToItem = useCallback((item) => {
    if (!item) {
      return;
    }

    const route = getGlobalSearchRoute(item.record);
    if (typeof route === 'string') {
      navigate(route);
    } else if (route?.path) {
      navigate(route.path, { state: route.state });
    }

    closeSearch();
  }, [closeSearch, navigate]);

  const wasOpenRef = useRef(open);

  useEffect(() => {
    onListeningChange?.(listening);
  }, [listening, onListeningChange]);

  useEffect(() => {
    const handleShortcut = (event) => {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const modifierPressed = isMac ? event.metaKey : event.ctrlKey;

      if (modifierPressed && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        if (disabled) {
          return;
        }
        onOpenChange?.(true);
      }

      if (event.key === 'Escape' && open) {
        closeSearch();
      }
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [closeSearch, disabled, onOpenChange, open]);

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      stopListening();
    }
    wasOpenRef.current = open;

    if (!open) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 50);

    return () => window.clearTimeout(timer);
  }, [open, stopListening]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, apiResults.length]);

  useEffect(() => {
    const trimmed = query.trim();

    if (!open || trimmed.length < 2) {
      setApiResults([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    let cancelled = false;

    const timer = window.setTimeout(async () => {
      try {
        const params = currentFarmId ? { q: trimmed, farm_id: currentFarmId } : { q: trimmed };
        const response = await api.get('/search', { params });
        if (!cancelled) {
          setApiResults(response.data?.results || []);
        }
      } catch {
        if (!cancelled) {
          setApiResults([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, query, currentFarmId]);

  const handleInputKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, Math.max(allSelectableItems.length - 1, 0)));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      navigateToItem(allSelectableItems[activeIndex]);
    }
  };

  const trimmedQuery = query.trim();
  const queryTooShort = trimmedQuery.length > 0 && trimmedQuery.length < 2;
  const showEmptyState = trimmedQuery.length >= 2 && !loading && allSelectableItems.length === 0;

  const shortcutLabel = navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl+K';

  return (
    <Modal
      open={open}
      onCancel={closeSearch}
      footer={null}
      width={720}
      className="global-search-modal"
      destroyOnClose
      closable={false}
      centered
      styles={{
        body: { padding: 0 },
        content: { overflow: 'hidden' },
      }}
    >
      <div className="global-search-panel">
        <div className="global-search-modal-header">
          <div className="global-search-modal-brand">
            <span className="global-search-modal-icon">
              <SearchOutlined />
            </span>
            <div>
              <h3 className="global-search-modal-title">{t('common.globalSearch')}</h3>
              <p className="global-search-modal-subtitle">
                Jump to animals, farms, contacts, alerts, and dashboards
              </p>
            </div>
          </div>
          <kbd className="global-search-modal-esc">ESC</kbd>
        </div>

        <div className={`global-search-input-wrap${listening ? ' is-listening' : ''}`}>
          <Input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={listening ? t('common.voiceSearchListening') : t('common.globalSearchInputPlaceholder')}
            prefix={<SearchOutlined className="global-search-input-prefix-icon" />}
            suffix={(
              <div className="global-search-input-suffix">
                {!trimmedQuery && !listening && (
                  <kbd className="global-search-input-kbd">{shortcutLabel}</kbd>
                )}
                <VoiceSearchButton
                  listening={listening}
                  onClick={toggleListening}
                />
              </div>
            )}
            allowClear
            size="large"
            className="global-search-input"
            variant="borderless"
          />
        </div>

        {trimmedQuery.length >= 2 && !loading && allSelectableItems.length > 0 && (
          <div className="global-search-results-meta">
            {allSelectableItems.length} result{allSelectableItems.length === 1 ? '' : 's'}
          </div>
        )}

        <div className="global-search-results">
          {loading && (
            <div className="global-search-loading">
              <Spin size="small" />
              <span>{t('common.searching')}</span>
            </div>
          )}

          {listening && !trimmedQuery && (
            <div className="global-search-hint global-search-listening-hint">
              {t('common.voiceSearchListening')}
            </div>
          )}

          {!trimmedQuery && !listening && (
            <div className="global-search-hint">
              {t('common.globalSearchHint')}
            </div>
          )}

          {queryTooShort && !listening && (
            <div className="global-search-hint">
              {t('common.globalSearchMinChars')}
            </div>
          )}

          {apiResults.map((section) => (
            <section key={section.key} className="global-search-section">
              <div className="global-search-section-title">{section.label}</div>
              <div className="global-search-list">
                {section.items.map((item) => {
                  const selectableIndex = allSelectableItems.findIndex(
                    (entry) => entry.key === `${section.key}-${item.type}-${item.id}`
                  );
                  const isActive = selectableIndex === activeIndex;

                  return (
                    <button
                      key={`${section.key}-${item.type}-${item.id}`}
                      type="button"
                      className={`global-search-item${isActive ? ' is-active' : ''}`}
                      onMouseEnter={() => setActiveIndex(selectableIndex)}
                      onClick={() => navigateToItem(allSelectableItems[selectableIndex])}
                    >
                      <span className={`global-search-item-icon is-type-${item.type || 'default'}`}>
                        {getSearchItemIcon(item.type)}
                      </span>
                      <span className="global-search-item-body">
                        <span className="global-search-item-title">{item.title}</span>
                        {item.subtitle && (
                          <span className="global-search-item-subtitle">{item.subtitle}</span>
                        )}
                      </span>
                      <span className="global-search-item-badge">{section.label}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}

          {showEmptyState && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('common.noSearchResults')}
              className="global-search-empty"
            />
          )}
        </div>

        <div className="global-search-footer">
          <span className="global-search-footer-hint">
            <kbd>↑</kbd>
            <kbd>↓</kbd>
            Navigate
          </span>
          <span className="global-search-footer-hint">
            <kbd>Enter</kbd>
            Open
          </span>
          <span className="global-search-footer-hint">
            <kbd>Esc</kbd>
            Close
          </span>
        </div>
      </div>
    </Modal>
  );
});

export default GlobalSearch;
